import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import { fabric } from "fabric";

import { useElementSize, useDebounce } from "usehooks-ts";

import Backend from "../../../utils/Backend";
import General from "../../../utils/General";

const ZOOM_ENABLED = false;
const PANNING_ENABLED = false;

const FRAME_BORDER_SIZE = 40;

const FRAME_BORDER_RADIUS = 40;

const CORNER_COLOR = "#FFC52F";

const DARK_FRAME_COLOR = "#0D0D0D";

const LONG_PRESS_DISTANCE_THRESHOLD = 25;

const Canvas = React.forwardRef((props, ref) => {
  let { frame, text, transform, order } = props.state;
  let { image } = props;

  const [canvas, setCanvas] = useState(null);

  const [distance, setDistance] = useState(0);

  const canvasRef = useRef(null);
  const [color, setColor] = useState(null);
  const initialLongPressPositionRef = useRef();
  const currentLongPressPositionRef = useRef();
  const isLongPressRef = useRef(false);

  const [canvasLoading, setCanvasLoading] = useState(true);
  const [isDoneLoading, setDoneLoading] = useState(false);

  const [viewport, setViewport] = useState(null);

  const [previewDivRef, previewDivSize] = useElementSize();

  const [isLoading, setIsLoading] = useState(false);

  const longPressTimeout = useRef();

  const debouncedSize = useDebounce(
    previewDivSize.width + previewDivSize.height,
    500
  );

  function getCanvasSize(containerSize, image) {
    let imageRatio = image.width / image.height;

    let maxWidth = containerSize.width;
    let maxHeight = containerSize.height;

    let zoomWidth = maxWidth / image.width;
    let zoomHeight = maxHeight / image.height;

    let defaultZoom = zoomWidth < zoomHeight ? zoomWidth : zoomHeight;

    const minZoom = defaultZoom;
    const maxZoom = minZoom * 10;

    updateViewport({
      image,
      minZoom,
      maxZoom,
      defaultZoom,
    });

    return {
      width: maxWidth,
      height: maxHeight,
    };
  }

  function getDistance(firstTouch, secondTouch) {
    let dx = firstTouch.pageX - secondTouch.pageX;
    let dy = firstTouch.pageY - secondTouch.pageY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function updateViewport(data) {
    setViewport((current) => ({
      ...current,
      ...data,
    }));
  }

  useEffect(() => {
    if (!previewDivSize.width || canvas) {
      return;
    }

    fabric.Image.fromURL(
      image.original,
      (oImg) => {
        oImg.objectCaching = false;

        if (!fabric.isSetupDone) {
          fabric.textureSize = Math.max(oImg.width, oImg.height);
          console.log("Texture====================================");
          console.log(fabric.textureSize);
          fabric.filterBackend = fabric.initFilterBackend();
          console.log("====================================");
          console.log(fabric.filterBackend);
          fabric.isSetupDone = true;
        }
        const canvasSize = getCanvasSize(previewDivSize, oImg);

        const options = {
          width: canvasSize.width,
          height: canvasSize.height,
          selection: false,
        };

        const fabricCanvas = new fabric.Canvas("fabric-canvas", options);

        fabricCanvas.renderAll();
        // timeout is to give enough time for the
        // renderAll above to resize the canvas
        setTimeout(() => {
          setCanvas(fabricCanvas);
        }, 500);

        return () => {
          fabricCanvas.dispose();
        };
      },
      { crossOrigin: "anonymous" }
    );
  }, [previewDivSize.width, canvas]);

  useEffect(() => {
    if (!canvas) {
      return;
    }

    let { image } = viewport;

    let imageProps = {
      width: image.width,
      height: image.height,
    };

    let background = new fabric.Rect({
      ...imageProps,
      fill: "rgb(0,0,0)",
      selectable: false,
      absolutePositioned: true,
      strokeWidth: 0,
    });

    let frame = new fabric.Rect({
      ...imageProps,
      fill: "rgb(0,0,0)",
      selectable: false,
      absolutePositioned: true,
      strokeWidth: 0,
    });

    updateViewport({
      frame,
      background,
      width: image.width,
      height: image.height,
      center: new fabric.Point(canvas.width / 2, canvas.height / 2),
    });

    image.strokeWidth = 0;

    canvas.add(background, image, frame);

    canvas.sendToBack(background);

    canvas.bringToFront(image);

    image.lockMovementX = true;
    image.lockMovementY = true;

    image.selectable = false;

    props.onLoaded(canvas, image);

    // disable as causes blurry image on save
    // setupListeners();

    updateText();

    updateFrame(canvas, frame);

    updateTransform();

    canvas.on("mouse:down", onLongPressStarted);
    canvas.on("mouse:up", onLongPressEnded);

    setTimeout(() => {
      setCanvasLoading(false);
    }, 300);

    return () => {
      canvas.off("mouse:down", onLongPressStarted);
      canvas.off("mouse:up", onLongPressEnded);
      if (canvas) {
        canvas.dispose();
        setCanvas(null);
      }
    };
  }, [canvas]);

  useEffect(() => {
    if (isDoneLoading) return;
    if (canvas && !canvasLoading) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage("project-loading");
      }
      props.onReady(true);
      setDoneLoading(true);
    }
  }, [canvas, canvasLoading, isDoneLoading]);

  useEffect(() => {
    if (!canvas) {
      return;
    }

    let { image, frame } = viewport;
    let frameType = props.state.frame;

    if (frameType === "darkframe" || frameType === "polaroid") {
      image.clipPath = frame;
    } else {
      image.clipPath = null;
    }
  }, [frame]);

  const onLongPressStarted = (e) => {
    if (e.target?.textAlign || (e.e.touches && e.e.touches.length == 2)) {
      return;
    }

    let longPressPosition = e.e.touches ? e.e.touches[0] : e.e;
    initialLongPressPositionRef.current = longPressPosition;

    longPressTimeout.current = setTimeout(() => {
      if (
        !initialLongPressPositionRef.current ||
        !currentLongPressPositionRef.current
      ) {
        props.onLongPressStart();
        isLongPressRef.current = true;
        return;
      }
      let distance = getDistance(
        initialLongPressPositionRef.current,
        currentLongPressPositionRef.current
      );
      setDistance(distance);
      if (distance < LONG_PRESS_DISTANCE_THRESHOLD) {
        props.onLongPressStart();
        isLongPressRef.current = true;
        return;
      }

      initialLongPressPositionRef.current = null;
      currentLongPressPositionRef.current = null;
      isLongPressRef.current = false;
    }, 500);
  };

  const onLongPressEnded = (e) => {
    longPressTimeout.current && clearTimeout(longPressTimeout.current);
    props.onLongPressEnd();
    initialLongPressPositionRef.current = null;
    currentLongPressPositionRef.current = null;
    isLongPressRef.current = false;
  };

  useEffect(() => {
    if (!canvas) {
      return;
    }
    updateText();
  }, [text.value, text.color, text.backgroundColor, text.font, order]);

  useEffect(() => {
    if (!canvas || !frame || !viewport?.background) {
      return;
    }

    updateFrame(canvas, frame);
  }, [frame, transform.rotation, viewport?.background]);

  useEffect(() => {
    if (!viewport?.background) {
      return;
    }

    centerObjects();
  }, [viewport?.background != null]);

  useEffect(() => {
    if (!canvas) {
      return;
    }

    updateTransform();
  }, [transform.rotation, transform.flipX]);

  useEffect(() => {
    if (canvas) {
      resizeCanvas();
    }
  }, [debouncedSize, viewport?.background]);

  function resizeCanvas() {
    if (!canvas || !viewport) {
      return;
    }

    let canvasSize = getCanvasSize(previewDivSize, viewport.image);

    let width = canvasSize.width;
    let height = canvasSize.height;

    canvas.setDimensions({
      width,
      height,
    });

    updateViewport({
      center: new fabric.Point(canvas.width / 2, canvas.height / 2),
    });

    if (viewport.background) {
      centerObjects();
    }
  }

  function centerObjects() {
    const viewportCenter = viewport.center;

    const viewportBackground = viewport.background;
    const viewportFrame = viewport.frame;
    const viewportImage = viewport.image;

    viewportBackground.setPositionByOrigin(viewportCenter, "center", "center");
    viewportFrame.setPositionByOrigin(viewportCenter, "center", "center");
    viewportImage.setPositionByOrigin(viewportCenter, "center", "center");

    if (frame === "polaroid") {
      const polaroidCenter = new fabric.Point(
        viewportCenter.x,
        viewportCenter.y - FRAME_BORDER_SIZE * 3
      );

      viewportFrame.setPositionByOrigin(polaroidCenter, "center", "center");

      viewportImage.setPositionByOrigin(polaroidCenter, "center", "center");
    }

    zoomToFitViewport(viewport);

    canvas.renderAll();
  }

  function zoomToFitViewport(viewport) {
    const viewportCenter = viewport.center;

    const viewportBackground = viewport.background;

    let zoomWidth = canvas.width / viewportBackground.width;
    let zoomHeight = canvas.height / viewportBackground.height;
    let zoom = Math.min(zoomWidth, zoomHeight);

    // The point in the canvas where the viewport center should be mapped to
    let canvasCenter = new fabric.Point(canvas.width / 2, canvas.height / 2);

    // Apply the zoom to the canvas centered on `canvasCenter`
    canvas.zoomToPoint(canvasCenter, zoom);

    // Adjust canvas transformation to center the viewport center
    let viewportCenterTransformed = fabric.util.transformPoint(
      viewportCenter,
      canvas.viewportTransform
    );
    let adjustedX = canvasCenter.x - viewportCenterTransformed.x;
    let adjustedY = canvasCenter.y - viewportCenterTransformed.y;

    canvas.viewportTransform[4] += adjustedX;
    canvas.viewportTransform[5] += adjustedY;
    canvas.requestRenderAll();
  }

  const setupListeners = () => {
    canvas.on("mouse:wheel", (opt) => {
      opt.e.preventDefault();
      opt.e.stopPropagation();
      if (opt.e.ctrlKey) {
        if (ZOOM_ENABLED) {
          handleZoom(opt, canvas);
        }
      } else {
        if (PANNING_ENABLED) {
          handleDrag(opt, canvas);
          canvas.requestRenderAll();
        }
      }
    });

    // mobile event listeners
    let pausePanning = false;
    let lastX = 0;
    let lastY = 0;
    let zoomStartScale = 0;

    const { minZoom } = viewport;

    canvas.on({
      "touch:gesture": function (e) {
        if (e.e.touches && e.e.touches.length == 2) {
          pausePanning = true;
          var point = new fabric.Point(e.self.x, e.self.y);
          if (e.self.state == "start") {
            zoomStartScale = canvas.getZoom();
          }
          var delta = zoomStartScale * e.self.scale;
          if (delta < minZoom) {
            delta = minZoom;
          }
          if (ZOOM_ENABLED) {
            canvas.zoomToPoint(point, delta);
          }

          pausePanning = false;
        } else if (e.e.touches && e.e.touches.length == 1) {
          currentLongPressPositionRef.current = e.e.touches[0];
        }
      },
      "mouse:move": function (e) {
        currentLongPressPositionRef.current = e.e;
      },
      "selection:created": function () {
        pausePanning = true;
      },
      "selection:cleared": function () {
        pausePanning = false;
      },
      "touch:drag": function (e) {
        if (
          PANNING_ENABLED &&
          pausePanning === false &&
          e.e.layerX !== undefined &&
          e.e.layerY !== undefined
        ) {
          if (isLongPressRef.current) {
            return;
          }

          let currentX = e.e.layerX;
          let currentY = e.e.layerY;
          let xChange = currentX - lastX;
          let yChange = currentY - lastY;

          const movementThreshold = 25;

          if (
            Math.abs(xChange) <= movementThreshold &&
            Math.abs(yChange) <= movementThreshold
          ) {
            var delta = new fabric.Point(xChange, yChange);
            canvas.relativePan(delta);
          }

          lastX = e.e.layerX;
          lastY = e.e.layerY;

          currentLongPressPositionRef.current = e.e.touches
            ? e.e.touches[0]
            : e.e;
        }
      },
    });
  };

  const handleZoom = (opt, state) => {
    var delta = opt.e.deltaY;
    var zoom = canvas.getZoom();

    const { minZoom } = viewport;

    var maxZoom = Math.max(
      canvas.originalWidth / canvas.width,
      canvas.originalHeight / canvas.height
    );

    zoom *= 0.999 ** delta;

    if (zoom > maxZoom) {
      zoom = maxZoom;
    }
    if (zoom < minZoom) {
      zoom = minZoom;
    }

    canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
  };

  const handleDrag = (opt, state) => {
    var e = opt.e;
    var vpt = state.viewportTransform;

    vpt[4] -= e.deltaX;
    vpt[5] -= e.deltaY;

    var boundingRect = state.calcViewportBoundaries();

    var maxX = boundingRect.tr.x;
    var maxY = boundingRect.bl.y;
    var minX = boundingRect.tl.x;
    var minY = boundingRect.tl.y;

    // Clamp the image position to stay within the canvas bounds
    vpt[4] = Math.max(minX, Math.min(maxX, vpt[4]));
    vpt[5] = Math.max(minY, Math.min(maxY, vpt[5]));
  };

  useEffect(() => {
    let viewportText = viewport?.text;

    if (!viewportText) {
      return;
    }

    setupTextListeners();
  }, [viewport?.text != null]);

  function setupTextListeners() {
    let viewportText = viewport.text;

    viewportText.on("changed", () => onTextChange(viewportText));

    viewportText.on("moving", () => onTextChange(viewportText));

    viewportText.on("scaling", () => onTextChange(viewportText));
  }

  function updateText() {
    let viewportText = viewport.text;

    if (!text.value && !viewportText) {
      return;
    }

    if (text.value == null && viewportText) {
      canvas.remove(viewportText);

      updateViewport({ text: null });

      return;
    }

    if (!viewportText) {
      viewportText = new fabric.Textbox(text.value, {
        editable: true,
      });

      viewportText.setControlsVisibility({
        mb: false,
        mt: false,
        mtr: false,
      });

      canvas.add(viewportText);
    }

    viewportText.set({
      text: text.value || "",
      fontSize: text.size,
      fill: text.color,
      backgroundColor: text.backgroundColor,
      fontFamily: text.font,
      left: text.left,
      top: text.top,
      width: text.width,
      height: text.height,
      scaleX: text.scaleX,
      scaleY: text.scaleY,
      textAlign: "center",
      cornerColor: CORNER_COLOR,
    });

    updateViewport({ text: viewportText });

    canvas.renderAll();
  }

  const onTextChange = (viewportText) => {
    props.onTextChange({
      ...text,
      value: viewportText.text,
      left: viewportText.left,
      top: viewportText.top,
      width: viewportText.width,
      height: viewportText.height,
      scaleX: viewportText.scaleX,
      scaleY: viewportText.scaleY,
      backgroundColor: viewportText.backgroundColor,
      color: viewportText.fill,
      font: viewportText.fontFamily,
    });
  };

  function updateFrame(canvas, frameType) {
    if (!viewport?.background) {
      return;
    }

    const { background: viewportBackground, frame: viewportFrame } = viewport;

    let backgroundWidth = viewport.width;
    let backgroundHeight = viewport.height;

    if (
      Math.abs(transform.rotation) === 90 ||
      Math.abs(transform.rotation) === 270
    ) {
      backgroundWidth = viewport.height;
      backgroundHeight = viewport.width;
    }

    let backgroundProps = {
      fill: "rgb(0,0,0)",
      width: backgroundWidth,
      height: backgroundHeight,
    };

    let frameProps = {
      width: backgroundWidth,
      height: backgroundHeight,
      fill: "rgb(0,0,0)",
      rx: 0,
      ry: 0,
    };

    if (frameType === "darkframe") {
      backgroundProps = {
        ...backgroundProps,
        width: frameProps.width + FRAME_BORDER_SIZE * 2,
        height: frameProps.height + FRAME_BORDER_SIZE * 2,
        fill: DARK_FRAME_COLOR,
        rx: FRAME_BORDER_RADIUS,
        ry: FRAME_BORDER_RADIUS,
      };
      frameProps = {
        ...frameProps,
        fill: "white",
        rx: FRAME_BORDER_RADIUS,
        ry: FRAME_BORDER_RADIUS,
      };
    } else if (frameType === "polaroid") {
      backgroundProps = {
        ...backgroundProps,
        width: frameProps.width + FRAME_BORDER_SIZE * 2,
        height: frameProps.height + FRAME_BORDER_SIZE * 8,
        fill: "white",
        rx: FRAME_BORDER_RADIUS,
        ry: FRAME_BORDER_RADIUS,
      };
      frameProps = {
        ...frameProps,
        fill: "white",
        rx: FRAME_BORDER_RADIUS,
        ry: FRAME_BORDER_RADIUS,
      };
    }

    viewportBackground.set({
      ...backgroundProps,
    });

    viewportFrame.set({
      ...frameProps,
    });

    centerObjects();

    canvas.renderAll();
  }

  function updateTransform() {
    const viewportImage = viewport.image;

    if (!viewportImage) {
      return;
    }

    let angle = transform.rotation % 360;

    viewportImage.set({
      flipX: transform.flipX,
    });

    viewportImage.rotate(angle);

    canvas.renderAll();
  }

  async function saveImage() {
    if (isLoading) return;
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 300));

    const transform = canvas.viewportTransform.slice();
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];

    const background = viewport.background;

    let meta = {
      title: "edit.png",
      width: background.width,
      height: background.height,
      left: background.left,
      top: background.top,
    };

    const snapshot = canvas.toDataURL({
      format: "image/png",
      quality: 1,
      multiplier: 1,
      ...meta,
    });

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("uploadImage:" + snapshot);
    } else {
      let file = General.dataURIToBlob(snapshot, "image/png");
      uploadImageFile(file, meta, snapshot);
    }

    canvas.viewportTransform = transform;
  }

  function uploadImageFile(file, meta) {
    Backend.uploadImage(file, meta, (percentage) => {
      broadcastProgress(percentage);
    })
      .then((image) => {
        setIsLoading(false);

        props.onImageSaved(image);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }

  function broadcastProgress(percentage) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(`upload-progress:${percentage}`);
    }
  }

  useImperativeHandle(ref, () => ({
    saveImage,
  }));

  return (
    <>
      <div
        id="preview"
        ref={previewDivRef}
        className={frame ? "preview " + frame : "preview"}
      >
        <canvas id="fabric-canvas" ref={canvasRef} />

        {(!canvas || canvasLoading) && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              backgroundColor: "black",
            }}
          >
            <h3>Loading Assets...</h3>
          </div>
        )}
      </div>
    </>
  );
});

export default Canvas;
